import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { map, filter, catchError, exhaustMap } from 'rxjs/operators';
import { CandidateUploadedProfileDocumentService } from 'src/app/recruiting/candidates/new-profile/candidate-uploaded-profile-document/candidate-uploaded-profile-document.service';
import { ProfileUnsignedUploadedDocument } from 'src/app/recruiting/candidates/new-profile/candidate-uploaded-profile-document/models/profile-unsigned-uploaded-document.model';
import { ProfileUnsigned } from 'src/app/recruiting/candidates/new-profile/candidate-uploaded-profile-document/models/profile-unsigned.model';
import { StandardDocumentMetaData } from 'src/app/recruiting/candidates/new-profile/candidate-uploaded-profile-document/models/standard-documents-meta-data';
import * as unsignedUploadDocumentActions from 'src/app/recruiting/candidates/new-profile/candidate-uploaded-profile-document/store/actions/unsigned-upload-document.actions';
import { selectUnsignedUploadDocumentFeature } from 'src/app/recruiting/candidates/new-profile/candidate-uploaded-profile-document/store/selectors/unsigned-upload-document.selectors';

@Injectable()
export class UnsignedUploadDocumentEffects {
    loadUnsignedUploadDocument$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(unsignedUploadDocumentActions.loadUnsignedDocuments),
            concatLatestFrom((action) =>
                this.store
                    .select(selectUnsignedUploadDocumentFeature)
                    .pipe(filter((docs) => action.candidateId !== docs?.candidateId))
            ),
            exhaustMap((action) => {
                return this._candidateUploadedProfileDocumentService
                    .getProfileUnsignedUploadedDocuments(action[0].candidateId)
                    .pipe(
                        map((response) => this.formatData(response)),
                        map((docs) =>
                            unsignedUploadDocumentActions.loadUnsignedDocumentsSuccess({
                                documents: docs.documents,
                                progress: docs.progress,
                                hasUnsignedFlowEnabled: docs.hasUnsignedFlowEnabled,
                                candidateId: action[0].candidateId
                            })
                        ),
                        catchError((error: unknown) =>
                            of(unsignedUploadDocumentActions.loadUnsignedDocumentsFail({ error }))
                        )
                    );
            })
        );
    });

    constructor(
        private readonly actions$: Actions,
        private readonly store: Store,
        private readonly _candidateUploadedProfileDocumentService: CandidateUploadedProfileDocumentService
    ) {}

    private formatData(standardDocumentMetaData: StandardDocumentMetaData): ProfileUnsigned {
        const profileUnsigned = {
            progress: standardDocumentMetaData.percent,
            hasUnsignedFlowEnabled: true,
            documents:
                standardDocumentMetaData.percent === 0
                    ? null
                    : standardDocumentMetaData.documents.map(
                          (doc) =>
                              ({
                                  documentName: doc.title,
                                  file: doc.documentId > 0 ? doc.fileUrl : null,
                                  dateAdded: doc.uploadedDate,
                                  id: doc.documentId,
                                  group: standardDocumentMetaData.percent < 100 ? this.getGroup(doc.documentId) : null
                              } as ProfileUnsignedUploadedDocument)
                      )
        } as ProfileUnsigned;

        return profileUnsigned;
    }

    private getGroup(documentId: number): string {
        if (documentId > 0) {
            return 'Uploaded';
        }
        return 'Missing';
    }
}
